import type {
  CatalogAutomation,
  CatalogUpdateControl,
  ApprovedImageStatus,
  CatalogPolicyAuditEvent,
  CatalogPolicyDetail,
  CatalogPolicySimulation,
  CatalogPolicySimulationEvidenceSummary,
  CatalogPolicySummary,
  CatalogPolicyValidation,
  DashboardSummary,
  ObservabilityReport,
  PortalJob,
  SessionUser,
} from "./types";


class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

export function getMaintenance(signal: AbortSignal): Promise<import("./Maintenance").MaintenanceReport> {
  return request("/api/maintenance", { signal });
}

export function isAuthenticationError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

function readCookie(name: string): string {
  const prefix = `${name}=`;
  const value = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return value ? decodeURIComponent(value.slice(prefix.length)) : "";
}

async function ensureCsrf(): Promise<string> {
  let token = readCookie("csrftoken");
  if (!token) {
    const response = await fetch("/api/auth/csrf", { credentials: "same-origin" });
    if (!response.ok) {
      throw new ApiError("No se pudo preparar la sesión segura.", response.status);
    }
    token = readCookie("csrftoken");
  }
  return token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    headers.set("X-CSRFToken", await ensureCsrf());
  }

  const response = await fetch(path, {
    ...init,
    method,
    headers,
    credentials: "same-origin",
  });
  if (response.status === 204) {
    return undefined as T;
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const fieldMessage = Object.entries(payload)
      .filter(([, value]) => Array.isArray(value))
      .map(([field, value]) => `${field}: ${(value as unknown[]).join(" ")}`)
      .join(" · ");
    const detail = typeof payload.detail === "string"
      ? payload.detail
      : fieldMessage || "La operación no pudo completarse.";
    throw new ApiError(detail, response.status);
  }
  return payload as T;
}

export async function getSession(): Promise<SessionUser | null> {
  try {
    return await request<SessionUser>("/api/auth/session");
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      return null;
    }
    throw error;
  }
}

export function login(identifier: string, password: string): Promise<SessionUser> {
  return request<SessionUser>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ identifier, password }),
  });
}

export function logout(): Promise<void> {
  return request<void>("/api/auth/logout", { method: "POST" });
}

export function getDashboardSummary(): Promise<DashboardSummary> {
  return request<DashboardSummary>("/api/dashboard/summary");
}

export function getObservability(): Promise<ObservabilityReport> {
  return request<ObservabilityReport>("/api/observability");
}

export function getJob(jobId: string): Promise<PortalJob> {
  return request<PortalJob>(`/api/jobs/${jobId}/`);
}

export function getCatalogAutomation(): Promise<CatalogAutomation> {
  return request<CatalogAutomation>("/api/catalog/automation");
}

export function requestCatalogDownload(): Promise<PortalJob> {
  return request<PortalJob>("/api/catalog/automation", { method: "POST" });
}

export function setCatalogAutomaticApproval(
  enabled: boolean,
): Promise<CatalogAutomation> {
  return request<CatalogAutomation>("/api/catalog/automation", {
    method: "PATCH",
    body: JSON.stringify({ automatic_approval_enabled: enabled }),
  });
}

export function setCatalogCompleteCycle(
  settings: { enabled?: boolean; intervalHours?: number },
): Promise<CatalogAutomation> {
  return request<CatalogAutomation>("/api/catalog/automation", {
    method: "PATCH",
    body: JSON.stringify({
      ...(settings.enabled === undefined ? {} : { complete_cycle_enabled: settings.enabled }),
      ...(settings.intervalHours === undefined ? {} : { complete_cycle_interval_hours: settings.intervalHours }),
    }),
  });
}

export function getApprovedImageStatus(): Promise<ApprovedImageStatus> {
  return request<ApprovedImageStatus>("/api/catalog/images/status");
}

export function getCatalogUpdateControl(signal?: AbortSignal): Promise<CatalogUpdateControl> {
  return request<CatalogUpdateControl>("/api/catalog/update-control", { signal });
}

export function startCatalogUpdate(): Promise<CatalogUpdateControl> {
  return request<CatalogUpdateControl>("/api/catalog/update-control/start", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function approveProductionCycle(runId: string, planSha256: string): Promise<CatalogUpdateControl> {
  return request<CatalogUpdateControl>("/api/catalog/update-control/approve", {
    method: "POST", body: JSON.stringify({ run_id: runId, plan_sha256: planSha256, confirm: true }),
  });
}

export function pauseCatalogUpdate(runId: string): Promise<CatalogUpdateControl> {
  return request<CatalogUpdateControl>("/api/catalog/update-control/stop", {
    method: "POST",
    body: JSON.stringify({ run_id: runId }),
  });
}

export function resumeCatalogUpdate(runId: string): Promise<CatalogUpdateControl> {
  return request<CatalogUpdateControl>("/api/catalog/update-control/resume", {
    method: "POST",
    body: JSON.stringify({ run_id: runId }),
  });
}

export function recoverCatalogUpdate(runId: string, action: "retry" | "restart"): Promise<CatalogUpdateControl> {
  return request<CatalogUpdateControl>(`/api/catalog/update-control/${action}`, {
    method: "POST",
    body: JSON.stringify({ run_id: runId, confirmed: action === "restart" }),
  });
}

export function requestCatalogRehearsal(targetKey = "restore-drill-clone"): Promise<PortalJob> {
  return request<PortalJob>("/api/catalog/rehearsals", {
    method: "POST",
    body: JSON.stringify({ target_key: targetKey }),
  });
}

export function getCatalogPolicies(): Promise<CatalogPolicySummary[]> {
  return request<CatalogPolicySummary[]>("/api/catalog/policies");
}

export function getCatalogPolicy(policyId: string): Promise<CatalogPolicyDetail> {
  return request<CatalogPolicyDetail>(`/api/catalog/policies/${policyId}`);
}

export function createCatalogPolicyDraft(): Promise<CatalogPolicyDetail> {
  return request<CatalogPolicyDetail>("/api/catalog/policies", {
    method: "POST",
    body: JSON.stringify({
      profile_key: "idirecto-prestashop-v2",
      environment: "test",
      schema_version: "abora-idirecto-transform-rules-v1",
      configuration: { schema: "abora-idirecto-transform-rules-v1" },
      comment: "Borrador creado desde el Portal del Empleado",
    }),
  });
}

export function updateCatalogPolicyDraft(
  policyId: string,
  expectedConfigSha256: string,
  configuration: Record<string, unknown>,
  comment: string,
): Promise<CatalogPolicyDetail> {
  return request<CatalogPolicyDetail>(`/api/catalog/policies/${policyId}`, {
    method: "PATCH",
    body: JSON.stringify({
      expected_config_sha256: expectedConfigSha256,
      configuration,
      comment,
    }),
  });
}

export function validateCatalogPolicy(policyId: string): Promise<CatalogPolicyValidation> {
  return request<CatalogPolicyValidation>(`/api/catalog/policies/${policyId}/validate`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function simulateCatalogPolicy(policyId: string): Promise<CatalogPolicySimulation> {
  return request<CatalogPolicySimulation>(`/api/catalog/policies/${policyId}/simulate`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function getCatalogPolicySimulations(
  policyId: string,
): Promise<CatalogPolicySimulationEvidenceSummary[]> {
  return request<CatalogPolicySimulationEvidenceSummary[]>(
    `/api/catalog/policies/${policyId}/simulations`,
  );
}

export function getCatalogPolicySimulation(
  policyId: string,
  evidenceId: string,
): Promise<CatalogPolicySimulation> {
  return request<CatalogPolicySimulation>(
    `/api/catalog/policies/${policyId}/simulations/${evidenceId}`,
  );
}

export function recordCatalogPolicyDecision(
  policyId: string,
  evidenceId: string,
  decision: "approve" | "reject",
  comment: string,
): Promise<CatalogPolicySimulation> {
  return request<CatalogPolicySimulation>(
    `/api/catalog/policies/${policyId}/simulations/${evidenceId}/decisions`,
    {
      method: "POST",
      body: JSON.stringify({ decision, comment }),
    },
  );
}

export function getCatalogPolicyAudit(policyId: string): Promise<CatalogPolicyAuditEvent[]> {
  return request<CatalogPolicyAuditEvent[]>(`/api/catalog/policies/${policyId}/audit`);
}
